import React, { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Input from './Input';
import Button from './Button';

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  searchable?: boolean;
  searchPlaceholder?: string;
  pageSize?: number;
  loading?: boolean;
  onRowClick?: (row: TData) => void;
  showPagination?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

function DataTable<TData>({
  data,
  columns,
  searchable = true,
  searchPlaceholder = "Search...",
  pageSize = 10,
  loading = false,
  onRowClick,
  showPagination = true,
  sorting: externalSorting,
  onSortingChange: onExternalSortingChange,
}: DataTableProps<TData>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Use external sorting if provided, otherwise use internal sorting
  const sorting = externalSorting || internalSorting;
  const setSorting = onExternalSortingChange ? 
    (updaterOrValue: any) => {
      if (typeof updaterOrValue === 'function') {
        onExternalSortingChange(updaterOrValue(externalSorting || []));
      } else {
        onExternalSortingChange(updaterOrValue);
      }
    } : setInternalSorting;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Only use pagination model if showPagination is true (client-side pagination)
    ...(showPagination && {
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        pagination: {
          pageSize,
        },
      },
    }),
  });

  // const pageSizeOptions = [
  //   { value: '5', label: '5 per page' },
  //   { value: '10', label: '10 per page' },
  //   { value: '20', label: '20 per page' },
  //   { value: '50', label: '50 per page' },
  // ];

  // Smart column width distribution based on number of columns
  const getColumnClass = (columnIndex: number, totalColumns: number) => {
    if (totalColumns >= 5) {
      // For 5+ columns, distribute equally
      return "";
    } else if (totalColumns === 2) {
      // For 2 columns: first column 30%, last column takes remaining space
      return columnIndex === 0 ? "w-[30%]" : "";
    } else if (totalColumns === 3) {
      // For 3 columns: first two columns get fixed width, last column takes remaining space
      return columnIndex < 2 ? "w-[25%]" : "";
    } else if (totalColumns === 4) {
      // For 4 columns: first three columns get fixed width, last column takes remaining space
      return columnIndex < 3 ? "w-[20%]" : "";
    }
    return "";
  };


  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="relative flex flex-col flex-1 min-h-0">
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
            <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-strong border border-gray-100">
              <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
              <p className="text-gray-600 text-sm font-medium mt-4">Loading data...</p>
            </div>
          </div>
        )}
      {/* Header with search only */}
      {searchable && (
        <div className="p-6 border-b border-gray-200 bg-white/80">
          <div className="flex justify-start">
            <div className="flex-1 max-w-sm">
              <Input
                placeholder={searchPlaceholder}
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-hidden min-h-0 relative">
        <div className="absolute inset-0 overflow-auto">
          <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className={`px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider bg-gray-50/50 ${getColumnClass(index, headerGroup.headers.length)}`}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={`flex items-center gap-2 ${
                          header.column.getCanSort() ? 'cursor-pointer select-none hover:text-primary-700 transition-colors duration-200' : ''
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <span className="flex flex-col ml-1">
                            <ChevronUpIcon 
                              className={`w-3 h-3 transition-colors duration-200 ${
                                header.column.getIsSorted() === 'asc' ? 'text-primary-600' : 'text-gray-400'
                              }`} 
                            />
                            <ChevronDownIcon 
                              className={`w-3 h-3 -mt-1 transition-colors duration-200 ${
                                header.column.getIsSorted() === 'desc' ? 'text-primary-600' : 'text-gray-400'
                              }`} 
                            />
                          </span>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200/60 bg-white/50">
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`hover:bg-white transition-colors duration-200 ${
                    onRowClick ? 'cursor-pointer hover:shadow-soft' : ''
                  }`}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <td key={cell.id} className={`px-6 py-4 text-sm text-gray-800 font-medium align-top ${getColumnClass(cellIndex, row.getVisibleCells().length)}`}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
          </table>
        </div>
      </div>

      {/* Pagination - Only show if showPagination is true */}
      {showPagination && (
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-white/80">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length
              )}{' '}
              of {table.getFilteredRowModel().rows.length} results
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="flex items-center gap-1"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Previous
              </Button>
              
              <div className="flex items-center gap-1 mx-2">
                {Array.from({ length: Math.min(5, table.getPageCount()) }, (_, i) => {
                  const pageIndex = i + Math.max(0, table.getState().pagination.pageIndex - 2);
                  if (pageIndex >= table.getPageCount()) return null;
                  
                  return (
                    <Button
                      key={pageIndex}
                      variant={pageIndex === table.getState().pagination.pageIndex ? 'primary' : 'ghost'}
                      size="sm"
                      onClick={() => table.setPageIndex(pageIndex)}
                    >
                      {pageIndex + 1}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="flex items-center gap-1"
              >
                Next
                <ChevronRightIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default DataTable;